import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { WarehousingMasterType, masterDataMap, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingInboundReceipt, WarehousingItem, WarehousingMasterData, WarehousingStockEntry } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-inbound-receipt-view',
  standalone: true,
  imports: [
    CommonModule, RouterModule, ButtonModule, ProgressBarModule, TableModule, TagModule,
    PageDataPanelComponent, PageDetailHeroComponent,
    PageFormSectionComponent, PageMetricStripComponent, PageShellComponent
  ],
  templateUrl: './warehousing-inbound-receipt-view.component.html'
})
export class WarehousingInboundReceiptViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private permissionService = inject(PermissionService);

  isLoading = false;
  receipt?: WarehousingInboundReceipt;
  items: WarehousingItem[] = [];
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  masterData = masterDataMap();
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Receipt Details', 'Receive Stock', '/warehousing/stock-entry');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  readonly statusSeverity = statusSeverity;
  readonly canEditStockEntries = this.permissionService.hasPermission(app_permissions.warehousing.stock_entry.edit);

  ngOnInit(): void {
    this.loadReceipt();
  }

  loadReceipt(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isInteger(id) || id <= 0) {
      return;
    }

    this.isLoading = true;
    forkJoin({
      receipt: this.warehousingService.getInboundReceipt(id),
      items: this.warehousingService.getItems(),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION'),
      uoms: this.warehousingService.getMasterData('TENANT_UOM')
    }).subscribe({
      next: response => {
        this.receipt = response.receipt.data;
        this.items = response.items.data || [];
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = response.warehouses.data || [];
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.masterData.TENANT_UOM = response.uoms.data || [];
        this.breadcrumbItems = warehousingBreadcrumb(
          this.receipt?.receiptNumber || 'Receipt Details',
          'Receive Stock',
          '/warehousing/stock-entry'
        );
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  metrics(): PageMetric[] {
    const receipt = this.receipt;
    return [
      { label: 'Receipt Lines', value: receipt?.lines?.length || receipt?.totalItems || 0 },
      { label: 'Base Quantity', value: this.formatQuantity(receipt?.totalQuantity), tone: 'success' },
      { label: 'DN Attachments', value: receipt?.attachments?.length || 0 },
      { label: 'Receipt Date', value: this.displayDate(receipt?.receiptDate) }
    ];
  }

  displayDate(value?: string | Date): string {
    if (!value) return '-';
    return value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
  }

  clientName(id?: number): string {
    return this.clients.find(client => client.id === id)?.companyName || '-';
  }

  accountName(id?: number): string {
    return this.accounts.find(account => account.id === id)?.companyName || '-';
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(value => value.id === id)?.name || '-';
  }

  itemFor(line: WarehousingStockEntry): WarehousingItem | undefined {
    return this.items.find(item => item.id === line.itemId);
  }

  itemLabel(line: WarehousingStockEntry): string {
    const item = this.itemFor(line);
    const itemNumber = line.itemNumber || item?.itemNumber;
    const itemName = line.itemName || item?.itemName;
    return [itemNumber, itemName].filter(Boolean).join(' | ') || `Item #${line.itemId}`;
  }

  itemDescription(line: WarehousingStockEntry): string {
    const description = this.itemFor(line)?.itemDescription;
    return description?.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || 'No description provided in Item Master.';
  }

  itemSize(line: WarehousingStockEntry): string {
    return itemSizeLabel(this.itemFor(line) || line) || 'No dimensions recorded';
  }

  itemDimensions(line: WarehousingStockEntry): string {
    const item = this.itemFor(line);
    const values = [
      item?.weightKg != null ? `${this.formatQuantity(item.weightKg)} kg` : '',
      item?.sqm != null ? `${this.formatQuantity(item.sqm)} sqm` : '',
      item?.cbm != null ? `${this.formatQuantity(item.cbm)} cbm` : ''
    ].filter(Boolean);
    return values.join(' | ') || '-';
  }

  enteredQuantity(line: WarehousingStockEntry): number {
    return line.transactionQuantity ?? line.quantity;
  }

  transactionUomName(line: WarehousingStockEntry): string {
    if (line.transactionUomId) {
      return this.masterName('TENANT_UOM', line.transactionUomId);
    }
    const item = this.itemFor(line);
    return item?.tenantUomName || this.masterName('TENANT_UOM', item?.tenantUomId) || 'Base UOM';
  }

  trackingMode(line: WarehousingStockEntry): string {
    const item = this.itemFor(line);
    return item?.trackingMode || (item?.isDisplayStand ? 'UNIT' : 'QUANTITY');
  }

  unitSkuLabel(line: WarehousingStockEntry): string {
    const unitSkus = [
      ...(line.unitSkus || []),
      ...(line.stockUnits || []).map(unit => unit.unitSku || unit.unitCode)
    ].filter((unitSku): unitSku is string => Boolean(unitSku?.trim()));
    return Array.from(new Set(unitSkus)).join(', ') || '-';
  }

  lineClientName(line: WarehousingStockEntry): string {
    return this.clientName(line.crmClientId || this.itemFor(line)?.crmClientId);
  }

  lineAccountName(line: WarehousingStockEntry): string {
    return this.accountName(line.crmAccountId || this.itemFor(line)?.crmAccountId);
  }

  lineCount(): number {
    return this.receipt?.lines?.length || 0;
  }

  downloadGrn(): void {
    if (!this.receipt?.id) return;
    this.isLoading = true;
    this.warehousingService.downloadGrnReport(this.receipt.id).subscribe({
      next: blob => {
        this.isLoading = false;
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `grn-${this.receipt?.receiptNumber || this.receipt?.id}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
      },
      error: () => this.isLoading = false
    });
  }

  private formatQuantity(value?: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value || 0);
  }
}
